import {IContextLayerQuery} from "../utility/geo/context-layer-query";
import {Interaction} from "ol/interaction";
import {default as OlEvent} from 'ol/events/Event';
import {Feature, Map as OlMap, MapBrowserEvent} from 'ol';
import {Pixel} from "ol/pixel";
import {Coordinate} from "ol/coordinate";
import {Vector} from "ol/layer";
import {toLonLat} from "ol/proj";

export interface MapBrowserEventLike {
    type: string;
    map: OlMap;
    pixel: Pixel;
    coordinate: Coordinate;
    originalEvent?: Event;
}

export interface IMapClickInteractionOptions {
	onClick(event : MapBrowserEvent | MapBrowserEventLike) : boolean;
	onDeactivate?();
}

export function simulatedClickEvent(pixel: Pixel, map: OlMap): MapBrowserEventLike {
    let browserEvent;

    try {
        browserEvent = new Event('click');
    } catch (e) {
        browserEvent = new CustomEvent('simulated-click' );
    }

    return {
        type: 'singleclick',
        map,
        pixel,
        coordinate: map.getCoordinateFromPixel(<any> pixel),
        originalEvent: browserEvent
    };
}

export abstract class AbstractMapClickInteraction extends Interaction {
    public abstract simulateEvent(ev: MapBrowserEvent | MapBrowserEventLike): void;

    public abstract skipNext();

    public abstract deactivate();
}

/**
 * Interactions that respond to singleclicks on the map
 */
export class MapClickInteraction extends AbstractMapClickInteraction {
	protected doSkipNext;
	protected options: IMapClickInteractionOptions;

	constructor(options : IMapClickInteractionOptions) {
		let onClick = options.onClick;

		let handleEvent = (ev : OlEvent) : boolean => {
			if (this.getActive() && ev.type == 'singleclick') {
				if (this.doSkipNext) {
					this.doSkipNext = false;
					return true;
				} else {
					return onClick(<MapBrowserEvent> ev);
				}
			}
			return true;
		};

		super({ handleEvent : handleEvent });
		this.options = options;
		this.doSkipNext = false;
	}

	public simulateEvent(ev: MapBrowserEvent | MapBrowserEventLike) {
	    this.options.onClick(ev);
    }

	public skipNext() {
		this.doSkipNext = true;
	}

	public deactivate() {
		if (this.options.onDeactivate) this.options.onDeactivate();
	}
}

export class FeatureQueryClickInteraction extends MapClickInteraction {
	// What to do with the returned features

    public INITIAL_HELP : string = "Click within features to query them.";
    public ACTIVE_HELP : string = 'Click on more features to add them to the query. <br/> To remove features from the' +
        ' query, click on them again.';

    constructor(
        protected drawToLayer: Vector,
        protected contextLayerQuery: IContextLayerQuery,
		protected handleAddFeatures: (feature: Feature[]) => void,
		protected handleRemoveFeatures: (feature: Feature) => void) {
        super({ onClick: (ev) => this.onClick(ev) });

        this.set('help', this.INITIAL_HELP);
        this.set('loading', false);
    }

    public onClick(event: MapBrowserEvent | MapBrowserEventLike): boolean {
        let mapProjection = event.map.getView().getProjection();
        let coord = toLonLat(event.coordinate, mapProjection);

        let featureFound = event.map.forEachFeatureAtPixel(event.pixel, (feature) => {
            return feature;
        }, {
            layerFilter: (l) => l == this.drawToLayer
        });
        if (featureFound instanceof Feature) {
            this.drawToLayer.getSource().removeFeature(featureFound);

            try {
                this.handleRemoveFeatures(featureFound);
			} catch (e) {

            }


            let hasFeatures = this.drawToLayer.getSource().getFeatures().length;
            if (hasFeatures) {
                this.set('help', this.ACTIVE_HELP);
            } else {
                // Deactivate this
                this.setActive(false);
            }
        } else {
            this.set('loading', true);
            let query = this.contextLayerQuery
                .queryPoint(coord)
                .catch((err) => {
                    this.set('error', err);
                    this.set('loading', false);
                    this.set('queryError', (this.get('queryError') || 0) + 1);
                });

            query.then((features) => {
                if (!features || !features.length) {
                    features = [];
                }

                this.set('loading', false);
                let src = this.drawToLayer.getSource();
                src.addFeatures(features);

                try {
                    this.handleAddFeatures(features);
				} catch (e) {

                }


                this.set('help', this.ACTIVE_HELP);
            });
        }
        return false;
    }
}

